import {
    Paper,
    Typography,
    List,
    ListItem,
    ListItemText,
    TextField,
    ListItemSecondaryAction,
    IconButton,
    Button,
    Box,
    Divider,
} from "@mui/material";
import { parseISO, format } from "date-fns";
import { pt } from "date-fns/locale";
import { memo } from "react";
import { AvailabilitySlot } from "../../utils/types";
import DeleteIcon from "@mui/icons-material/Delete";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
const getServiceCounts = (selectedSlots: Array<AvailabilitySlot>) => {
    const serviceCounts = selectedSlots.reduce((accumulator, slot) => {
        const serviceName = slot.serviceName;
        accumulator[serviceName] = (accumulator[serviceName] || 0) + 1;
        return accumulator;
    }, {} as { [serviceName: string]: number });

    return serviceCounts;
};

type SelectedSlotsListProps = {
    selectedSlots: Array<AvailabilitySlot>;
    slotTitles: { [slotId: string]: string };
    onTitleChange: (slotId: string, value: string) => void;
    onRemoveSlot: (slot: AvailabilitySlot | AvailabilitySlot[]) => void;
    onCreateSessions?: () => void;
};

export const SelectedSlotsList = memo(
    ({ selectedSlots, slotTitles, onTitleChange, onRemoveSlot, onCreateSessions }: SelectedSlotsListProps) => {
        const serviceCounts = getServiceCounts(selectedSlots);
        const serviceEntries = Object.entries(serviceCounts);

        return (
            <Paper
                elevation={6}
                sx={{
                    position: "fixed",
                    bottom: 100,
                    right: 32,
                    zIndex: 1200,
                    width: 800,
                    maxHeight: 600,
                    overflowY: "auto",
                    p: 4,
                    borderRadius: 3,
                    backgroundColor: "background.paper",
                }}
            >
 
                <Typography variant="subtitle1" color="primary" sx={{ mb: 1, fontWeight: 600, ml: 3 }}>
                    Sessões Selecionadas
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, ml: 3 }}>
                    {serviceEntries.map(([serviceName, count], index) => (
                        <Box key={serviceName} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography variant="body2" color="primary">
                                {serviceName}: {count}
                            </Typography>
                            {index < serviceEntries.length - 1 && (
                                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                            )}
                        </Box>
                    ))}
                </Box>
                <List dense disablePadding>
                    {selectedSlots.map((slot: AvailabilitySlot) => {
                        const startDate = typeof slot.start_date === "string" ? parseISO(slot.start_date) : slot.start_date;
                        const weekDay = format(startDate, "EEEE", { locale: pt });
                        const dayMonth = format(startDate, "d MMM", { locale: pt });
                        const time = format(startDate, "HH:mm");
                        const userName = slot.user ? `${slot.user?.first_name} ${slot.user?.last_name}` : "";

                        return (
                            <ListItem key={slot.slotId} sx={{ alignItems: "center" }}>
                                <ListItemText 
                                    primary={
                                        <Typography component="span" color="text.secondary">
                                            <Typography component="span" color="text.primary" fontWeight="bold">{weekDay}</Typography>
                                            {` ${dayMonth} `}
                                            <Typography component="span" color="text.primary" fontWeight="bold">{time}</Typography>
                                            {` — ${slot.serviceName}`}
                                        </Typography>
                                    }
                                    secondary={userName} 
                                />
                                <TextField
                                    size="small"
                                    label="Título da sessão"
                                    value={slotTitles[slot.slotId] || ""}
                                    onChange={(e) => onTitleChange(slot.slotId, e.target.value)}
                                    sx={{ minWidth: 120, mr: 1 }}
                                />
                                <ListItemSecondaryAction>
                                    <IconButton edge="end" aria-label="remover" onClick={() => onRemoveSlot(slot)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        );
                    })}
                </List>
                {onCreateSessions && (
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, mr: 4 }}>
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<EventAvailableIcon />}
                            onClick={onCreateSessions}
                        >
                            Criar sessões
                        </Button>
                    </Box>
                )}
            </Paper>
        );
    }
);
